import axios, { AxiosInstance, AxiosRequestConfig, AxiosResponse, InternalAxiosRequestConfig } from 'axios';
import Cookies from 'js-cookie';

// Extend AxiosRequestConfig to include metadata
declare module 'axios' {
  export interface InternalAxiosRequestConfig {
    metadata?: {
      startTime: Date;
    };
  }
}

// API Configuration
const API_BASE_URL = import.meta.env.VITE_API_URL || 'http://localhost:5000';
const API_TIMEOUT = 30000; // 30 seconds

// Token storage keys
const TOKEN_KEY = 'auth_token';
const CSRF_TOKEN_KEY = 'csrf_token';

/**
 * Secure API client with authentication and error handling
 */
class ApiClient {
  private client: AxiosInstance;
  private csrfToken: string | null = null;

  constructor() {
    this.client = axios.create({
      baseURL: API_BASE_URL,
      timeout: API_TIMEOUT,
      withCredentials: true, // Include cookies for session-based auth
      headers: {
        'Content-Type': 'application/json',
      },
    });

    this.setupInterceptors();
    this.initializeCSRFToken();
  }

  /**
   * Setup request and response interceptors
   */
  private setupInterceptors(): void {
    // Request interceptor
    this.client.interceptors.request.use(
      (config) => {
        // Add authentication token
        const token = this.getStoredToken();
        if (token) {
          config.headers.Authorization = `Bearer ${token}`;
        }

        // Add CSRF token for non-GET requests
        if (config.method !== 'get' && this.csrfToken) {
          config.headers['X-CSRF-Token'] = this.csrfToken;
        }

        // Add request timestamp for debugging
        config.metadata = { startTime: new Date() };

        return config;
      },
      (error) => {
        console.error('❌ Request interceptor error:', error);
        return Promise.reject(error);
      }
    );

    // Response interceptor
    this.client.interceptors.response.use(
      (response: AxiosResponse) => {
        // Update CSRF token if provided
        const newCSRFToken = response.headers['x-csrf-token'];
        if (newCSRFToken) {
          this.setCSRFToken(newCSRFToken);
        }

        return response;
      },
      (error) => {
        // Handle authentication errors
        if (error.response?.status === 401) {
          this.handleAuthError();
        }

        // Handle CSRF errors
        if (error.response?.status === 403 &&
            error.response?.data?.code === 'CSRF_INVALID') {
          this.refreshCSRFToken();
        }

        return Promise.reject(this.formatError(error));
      }
    );
  }

  /**
   * Initialize CSRF token
   */
  private async initializeCSRFToken(): Promise<void> {
    try {
      const storedToken = Cookies.get(CSRF_TOKEN_KEY);
      if (storedToken) {
        this.csrfToken = storedToken;
      } else {
        await this.refreshCSRFToken();
      }
    } catch (error) {
      console.warn('Failed to initialize CSRF token:', error);
    }
  }

  /**
   * Refresh CSRF token
   */
  private async refreshCSRFToken(): Promise<void> {
    try {
      const response = await this.client.get('/auth/csrf-token');
      const token = response.data.csrfToken;
      if (token) {
        this.setCSRFToken(token);
      }
    } catch (error) {
      console.error('Failed to refresh CSRF token:', error);
    }
  }

  /**
   * Set CSRF token
   */
  private setCSRFToken(token: string): void {
    this.csrfToken = token;
    Cookies.set(CSRF_TOKEN_KEY, token, {
      secure: process.env.NODE_ENV === 'production',
      sameSite: 'strict',
      expires: 1 // 1 day
    });
  }

  /**
   * Get stored authentication token
   */
  private getStoredToken(): string | null {
    if (typeof window === 'undefined') return null;
    return localStorage.getItem(TOKEN_KEY);
  }

  /**
   * Store authentication token
   */
  public setAuthToken(token: string): void {
    if (typeof window === 'undefined') return;
    localStorage.setItem(TOKEN_KEY, token);
  }

  /**
   * Remove authentication token
   */
  public removeAuthToken(): void {
    if (typeof window === 'undefined') return;
    localStorage.removeItem(TOKEN_KEY);
    Cookies.remove(CSRF_TOKEN_KEY);
    this.csrfToken = null;
  }

  /**
   * Clear authentication token (alias for removeAuthToken)
   */
  public clearAuthToken(): void {
    this.removeAuthToken();
  }

  /**
   * Handle authentication errors
   */
  private handleAuthError(): void {
    this.removeAuthToken();

    // Redirect to login if not already there
    if (typeof window !== 'undefined' && !window.location.pathname.includes('/login')) {
      window.location.href = '/login';
    }
  }

  /**
   * Format error for consistent handling
   */
  private formatError(error: any): ApiError {
    const response = error.response;

    return {
      message: response?.data?.message || error.message || 'An unexpected error occurred',
      code: response?.data?.code || 'UNKNOWN_ERROR',
      status: response?.status || 0,
      details: response?.data?.errors || null,
      originalError: error,
    };
  }

  /**
   * Generic request method
   */
  public async request<T = any>(config: AxiosRequestConfig): Promise<ApiResponse<T>> {
    try {
      const response = await this.client.request<T>(config);
      return {
        data: response.data,
        success: true,
        status: response.status,
      };
    } catch (error) {
      throw error; // Will be handled by interceptor
    }
  }

  /**
   * GET request
   */
  public async get<T = any>(url: string, config?: AxiosRequestConfig): Promise<ApiResponse<T>> {
    return this.request<T>({ ...config, method: 'GET', url });
  }

  /**
   * POST request
   */
  public async post<T = any>(url: string, data?: any, config?: AxiosRequestConfig): Promise<ApiResponse<T>> {
    return this.request<T>({ ...config, method: 'POST', url, data });
  }

  /**
   * PUT request
   */
  public async put<T = any>(url: string, data?: any, config?: AxiosRequestConfig): Promise<ApiResponse<T>> {
    return this.request<T>({ ...config, method: 'PUT', url, data });
  }

  /**
   * DELETE request
   */
  public async delete<T = any>(url: string, config?: AxiosRequestConfig): Promise<ApiResponse<T>> {
    return this.request<T>({ ...config, method: 'DELETE', url });
  }

  /**
   * Check if user is authenticated
   */
  public isAuthenticated(): boolean {
    return !!this.getStoredToken();
  }

  /**
   * Get current authentication status
   */
  public async getAuthStatus(): Promise<AuthStatus> {
    try {
      const response = await this.get<AuthStatusResponse>('/auth/status');
      return {
        authenticated: response.data.authenticated,
        user: response.data.user || null,
      };
    } catch (error: any) {
      return {
        authenticated: false,
        user: null,
      };
    }
  }
}

// Types
export interface ApiResponse<T = any> {
  data: T;
  success: boolean;
  status: number;
}

export interface ApiError {
  message: string;
  code: string;
  status: number;
  details?: any;
  originalError?: any;
}

export interface AuthStatus {
  authenticated: boolean;
  user: User | null;
}

export interface AuthStatusResponse {
  success: boolean;
  authenticated: boolean;
  user?: User;
}

export interface User {
  id: string;
  email: string;
  name: string;
  firstName?: string;
  lastName?: string;
  profilePic?: string;
  firstimeSetup: number;
  isOnboarded?: boolean;
  role: string;
  phone?: string;
  location?: string;
  birthday?: string;
  bio?: string;
  joinDate?: string;
}

// Create and export singleton instance
const apiClient = new ApiClient();
export default apiClient;
